import { deleteRequest, deleteRequestWithBody, getRequest, postRequest, putRequest } from '../../../core/utils/api';
import { type KeycloakContext } from '../../authentication/keycloak/KeycloakProvider';
import PermittedUser from '../models/permitted-user';
import showStatusCodeError from './logging.service';

export const getAllUsersByOfficeId = async (officeId: string, keycloak: KeycloakContext): Promise<PermittedUser[] | undefined> => {
  const apiResponse = await getRequest<PermittedUser[]>(`permitted-user?OfficeId=${officeId}`, keycloak);
  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return undefined;
  }

  return apiResponse.json;
};

/** The name already on file for an address, when the caller's own company knows it. */
export interface KnownUserName {
  readonly isKnown: boolean;
  readonly firstName?: string;
  readonly lastName?: string;
}

/**
 * Looks up the name already recorded for an address.
 *
 * Answers only for addresses the caller's own company already knows — deliberately, because a
 * lookup that answered for any address at all would let an administrator discover who is behind a
 * stranger's email without adding them to anything. The address goes in the body rather than the
 * query string so it does not end up in an access log.
 */
export const getKnownUserName = async (
  officeId: string,
  email: string,
  keycloak: KeycloakContext,
): Promise<KnownUserName | undefined> => {
  const apiResponse = await postRequest<KnownUserName>('permitted-user/known-name', keycloak, {
    OfficeId: officeId,
    Email: email,
  });

  // Silent on failure: this only pre-fills a convenience field, and a toast for it would interrupt
  // the administrator mid-typing for something they can simply fill in themselves.
  if (apiResponse.error) {
    return undefined;
  }

  return apiResponse.json;
};

/** What the server actually stored, which is not always what was sent. */
export interface CreatedPermittedUser {
  readonly id: string;
  readonly firstName?: string;
  readonly lastName?: string;
}

/**
 * Adds a permitted user to an office.
 *
 * `firstName` and `lastName` are optional and are stored on the person's own `Users` profile — the
 * single place a name lives, so one address cannot end up with different names in different
 * offices. A name they have already set themselves is never overwritten, which is why the response
 * carries the resulting name rather than the caller assuming its own input was accepted.
 */
export const createUser = async (
  officeId: string,
  email: string,
  keycloak: KeycloakContext,
  firstName?: string,
  lastName?: string,
): Promise<CreatedPermittedUser | undefined> => {
  const apiResponse = await postRequest<CreatedPermittedUser>('permitted-user', keycloak, {
    OfficeId: officeId,
    Email: email,
    FirstName: firstName?.trim() || undefined,
    LastName: lastName?.trim() || undefined,
  });
  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return undefined;
  }

  return apiResponse.json;
};

/** An office the person keeps access to after this removal. */
export interface OtherOffice {
  readonly officeId: string;
  readonly name: string;
}

/**
 * What removing a permitted user would cancel, counted before it happens.
 *
 * Removing somebody takes their access away, and every future booking they hold rests on that
 * access — so the bookings go with it. The counts come from the server, resolved in the office's
 * own timezone: the browser cannot work out where "today" starts for an office on the other side
 * of the world, and deriving the boundary here would put a different day in the dialog from the
 * one the removal acts on.
 */
export interface PermittedUserDeletionImpact {
  readonly officeUserId: string;
  readonly officeId: string;
  /** The office being acted on, so the dialog can name it rather than imply "everywhere". */
  readonly officeName: string;
  readonly email: string;
  readonly from: string;
  readonly timeZone: string;
  /**
   * Today at the office, `yyyy-MM-dd`. Stated separately from `from`, which is whatever date the
   * counting started at — they part company as soon as the scheduling dialog names a date.
   */
  readonly today: string;
  /**
   * The first date that disrupts nothing already booked. A warning line, not a floor: an
   * offboarding dated earlier is allowed and is the ordinary case for a leaver.
   */
  readonly earliestOutsideBookingWindow: string;
  readonly planPeriodDays: number;
  readonly bookings: number;
  readonly days: number;
  readonly groupReservations: number;
  /** Bookings of groups this colleague leads. Cancelled only where the group itself is deleted. */
  readonly groupReservationsLed: number;
  /**
   * Groups this colleague leads alone, which the removal deletes — a group with no leader left can
   * be edited by nobody at all. The one count here that describes something happening to other
   * people, and the reason it makes the removal impactful.
   */
  readonly groupsDissolved: number;
  /** Bookings cancelled for everybody on them because their group is one of those deleted. */
  readonly groupReservationsCancelledWithDissolvedGroups: number;
  readonly facilityReservations: number;
  /**
   * How many other offices this same person still reaches. Counted across companies, because the
   * honest answer to "does this removal finish the job" does not stop at a tenant boundary.
   */
  readonly otherOfficeCount: number;
  /**
   * The subset of those offices that may be named — the ones under the same company. A row can end
   * up under a different company, and naming another customer's office here would be a
   * cross-tenant disclosure. So this list can legitimately be shorter than `otherOfficeCount`.
   */
  readonly otherOffices: readonly OtherOffice[];
  /**
   * Whether these counts describe an all-offices removal. Echoed by the server rather than assumed
   * from what was asked, because the two can part company: a request for all offices can honestly
   * come back with none the caller may also act on.
   */
  readonly allOffices: boolean;
  /** Of `otherOfficeCount`, how many an all-offices removal would actually reach. */
  readonly otherOfficesRemovable: number;
  /** The rest — offices the person keeps, because this administrator does not administer them. */
  readonly otherOfficesNotPermittedCount: number;
  /** Those offices by name, filtered to the same company, so shorter than the count is normal. */
  readonly otherOfficesNotPermitted: readonly OtherOffice[];
  readonly hasImpact: boolean;
}

/**
 * Addressed by office-user id, not by email: a query string ends up in the access log, and that
 * log outlives the record it describes.
 */
export const getPermittedUserDeletionImpact = async (
  officeUserId: string,
  keycloak: KeycloakContext,
  from?: string,
  allOffices = false,
): Promise<PermittedUserDeletionImpact | undefined> => {
  // `from` is for the scheduling dialog: counted from the chosen date rather than from today,
  // because the weeks a leaver still works are not cancelled by anything and asking somebody to
  // acknowledge cancelling them would be asking them to agree to something untrue. Passed through
  // as the `yyyy-MM-dd` string the server parses — never a Date, which would reintroduce the
  // timezone the office-local boundary exists to avoid.
  //
  // `allOffices` widens the same counting to every other office this administrator may also remove
  // the person from. It has to be asked of the server rather than added up in the browser: which
  // offices those are is an authorisation question, and the numbers are what gets acknowledged.
  const params = new URLSearchParams();

  if (from) params.append('from', from);
  if (allOffices) params.append('allOffices', 'true');

  const query = params.toString() ? `?${params.toString()}` : '';

  const apiResponse = await getRequest<PermittedUserDeletionImpact>(
    `permitted-user/${officeUserId}/deletion-impact${query}`,
    keycloak,
  );

  if (apiResponse.error) {
    // Deliberately silent: the caller shows the failure inside the dialog, because a toast that
    // disappears behind an open modal would leave the delete button looking merely disabled.
    return undefined;
  }

  return apiResponse.json;
};

/** What a removal actually did, so the confirmation can state it rather than imply it. */
export interface DeleteUserOutcome {
  /** Offices the person was removed from. One for an ordinary single-office removal. */
  readonly officesRemovedFrom: number;
  /**
   * Offices left as they were because this administrator does not administer them. Reported, not
   * treated as a failure: the part that could be done was done, and somebody has to be told the
   * rest was not.
   */
  readonly officesNotPermitted: number;
}

/**
 * Removes a permitted user, acknowledging what it cancels.
 *
 * `acknowledged` is not decoration. The server refuses with 428 when the person still has future
 * bookings and the flag is absent, so nobody cancels a colleague's next three weeks by accident.
 *
 * `allOffices` applies the same removal to every other office this administrator already
 * administers — realm whyellow reaches all of them. It widens the reach, never the rights: offices
 * out of reach are left untouched and counted in the outcome. Ask the impact with the same flag
 * first, or the acknowledgement will be for a smaller action than the one taken.
 */
export const deleteUser = async (
  officeId: string,
  email: string,
  keycloak: KeycloakContext,
  acknowledged = false,
  allOffices = false,
): Promise<DeleteUserOutcome | undefined> => {
  const apiResponse = await deleteRequestWithBody<DeleteUserOutcome>('permitted-user', keycloak, {
    OfficeId: officeId,
    Email: email,
    Acknowledged: acknowledged,
    AllOffices: allOffices,
  });

  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return undefined;
  }

  // An older server answers 200 with a body this client cannot read every field of; the removal
  // still happened, so the counts fall back rather than the whole call reading as a failure.
  return {
    officesRemovedFrom: apiResponse.json?.officesRemovedFrom ?? 1,
    officesNotPermitted: apiResponse.json?.officesNotPermitted ?? 0,
  };
};

/** The person's offboarding state, and the dates the office judged it against. */
export interface PermittedUserAccessEnd {
  readonly officeUserId: string;
  readonly officeId: string;
  readonly officeName: string;
  readonly email: string;
  /** The scheduled date, or null once it has been called off. */
  readonly accessEndsOn?: string | null;
  readonly accessEndAppliedAt?: string | null;
  /** Today at the office. Resolved there, because the browser's today may be a different day. */
  readonly today: string;
  /** The first date that disrupts nothing already booked. Not a floor — a warning line. */
  readonly earliestOutsideBookingWindow: string;
  readonly planPeriodDays: number;
  readonly timeZone: string;
  readonly insideBookingWindow: boolean;
}

/** What a scheduling attempt did, or why it was refused. */
export interface AccessEndOutcome {
  readonly scheduled?: PermittedUserAccessEnd;
  /** The server's own sentence. Shown as-is; the generic fallback is a last resort. */
  readonly error?: string;
  /** The stable token, for the one refusal the dialog acts on rather than merely reports. */
  readonly code?: string;
}

/**
 * Schedules a person's removal for a date.
 *
 * Nothing is released now — they book and check in exactly as before until the day arrives. The
 * refusals are all expected states rather than faults: 428 when they hold bookings on or after the
 * date and nothing has been acknowledged, 486 when the date falls inside the period colleagues can
 * still book. Both are answered by retrying with a flag, which is why the code comes back rather
 * than only the message.
 */
export const setPermittedUserAccessEnd = async (
  officeUserId: string,
  accessEndsOn: string,
  keycloak: KeycloakContext,
  acknowledged: boolean,
  allowInsideBookingWindow: boolean,
): Promise<AccessEndOutcome> => {
  const apiResponse = await putRequest<PermittedUserAccessEnd>(`permitted-user/${officeUserId}/access-end`, keycloak, {
    AccessEndsOn: accessEndsOn,
    Acknowledged: acknowledged,
    AllowInsideBookingWindow: allowInsideBookingWindow,
  });

  if (apiResponse.error) {
    return { error: apiResponse.error, code: apiResponse.errorBody?.code };
  }

  return { scheduled: apiResponse.json };
};

/** Calls off a scheduled removal. Refused once the job has applied it — add the person back instead. */
export const cancelPermittedUserAccessEnd = async (
  officeUserId: string,
  keycloak: KeycloakContext,
): Promise<AccessEndOutcome> => {
  const apiResponse = await deleteRequest<PermittedUserAccessEnd>(`permitted-user/${officeUserId}/access-end`, keycloak);

  if (apiResponse.error) {
    return { error: apiResponse.error, code: apiResponse.errorBody?.code };
  }

  return { scheduled: apiResponse.json };
};

/** What a rename did to the person's sign-in account. Mirrors the server's enum. */
export type KeycloakNameOutcome = 'NotAttempted' | 'Updated' | 'NoAccount' | 'Failed';

/** The name as stored, plus what became of the sign-in account. */
export interface PermittedUserName {
  readonly officeUserId: string;
  readonly officeId: string;
  readonly email: string;
  readonly firstName?: string;
  readonly lastName?: string;
  /** True when this created the person's profile row — they had none until now. */
  readonly userCreated: boolean;
  /** True when a previously unlinked office row now points at that profile. */
  readonly userLinked: boolean;
  readonly keycloak: KeycloakNameOutcome;
}

export interface SetPermittedUserNameOutcome {
  readonly saved?: PermittedUserName;
  readonly error?: string;
  readonly code?: string;
}

/**
 * Corrects the name held for a colleague.
 *
 * The name is **global**: one record shared by every office they belong to, and the one the app's
 * colleague lists, groups and reservations display. There is no per-office display name, so this
 * renames them everywhere — which is why the dialog says so before it is sent.
 *
 * Either field may be blank, which clears it. The server trims, treats blank as absent, and pushes
 * the result to the app and portal Keycloak realms. That push is best-effort — the database is the
 * source of truth — so the outcome comes back rather than being assumed, and the caller reports it.
 */
export const setPermittedUserName = async (
  officeUserId: string,
  firstName: string,
  lastName: string,
  keycloak: KeycloakContext,
): Promise<SetPermittedUserNameOutcome> => {
  const apiResponse = await putRequest<PermittedUserName>(`permitted-user/${officeUserId}/name`, keycloak, {
    FirstName: firstName,
    LastName: lastName,
  });

  if (apiResponse.error) {
    return { error: apiResponse.error, code: apiResponse.errorBody?.code };
  }

  return { saved: apiResponse.json };
};

/** The per-office details an administrator keeps for a colleague. */
export interface PermittedUserDetails {
  readonly officeUserId: string;
  readonly officeId: string;
  readonly email: string;
  readonly sharedEmail?: string | null;
  readonly phoneNumber?: string | null;
  readonly isCompanyMedic: boolean;
}

/** Fields to change. Omitted means "leave as it is"; a blank string clears the text fields. */
export interface PermittedUserDetailsChange {
  readonly sharedEmail?: string;
  readonly phoneNumber?: string;
  readonly isCompanyMedic?: boolean;
}

export interface SetPermittedUserDetailsOutcome {
  readonly saved?: PermittedUserDetails;
  readonly error?: string;
  readonly code?: string;
}

/**
 * Sets the shared email, phone number and company medic flag held for a colleague in this office.
 *
 * Unlike the name these live on the office membership, so this changes one office only. Only the
 * fields given are sent, which lets the listing flip the medic switch on its own.
 */
export const setPermittedUserDetails = async (
  officeUserId: string,
  change: PermittedUserDetailsChange,
  keycloak: KeycloakContext,
): Promise<SetPermittedUserDetailsOutcome> => {
  const apiResponse = await putRequest<PermittedUserDetails>(`permitted-user/${officeUserId}/details`, keycloak, {
    SharedEmail: change.sharedEmail,
    PhoneNumber: change.phoneNumber,
    IsCompanyMedic: change.isCompanyMedic,
  });

  if (apiResponse.error) {
    return { error: apiResponse.error, code: apiResponse.errorBody?.code };
  }

  return { saved: apiResponse.json };
};
